'use client';

import { create } from 'zustand';
import type { Connection } from './type';
import {
  getConnections,
  createConnection,
  deleteConnection,
  updateKey,
  cancelDeletion,
  validateKey,
  getSettings,
  updateSettings
} from '@/layout/connections/api';
import type {
  CreateConnectionRequest,
  UpdateKeyRequest,
  ValidateKeyRequest,
  ValidateKeyResponse,
} from '@/types/api';
import { APIError } from '@/hooks/useApiError';

interface SyncInfo {
  taskId: string;
  marketplace: string;
}

interface ConnectionsState {
  connections: Connection[];
  currentConnectionId: string | null;
  isLoading: boolean;
  error: APIError | null;
  pendingSyncInfo: SyncInfo | null;

  // Actions
  fetchConnections: () => Promise<void>;
  setCurrentConnection: (uuid: string) => Promise<void>;
  addConnection: (data: CreateConnectionRequest) => Promise<Connection>;
  updateConnectionKey: (uuid: string, data: UpdateKeyRequest) => Promise<void>;
  requestDeletion: (uuid: string) => Promise<void>;
  cancelConnectionDeletion: (uuid: string) => Promise<void>;
  validateConnectionKey: (data: ValidateKeyRequest) => Promise<ValidateKeyResponse>;
  clearError: () => void;
  setPendingSyncInfo: (info: SyncInfo | null) => void;
}

// Парсинг структурированных ошибок API из ответа бэкенда
function parseAPIError(error: unknown): APIError {
  if (error && typeof error === 'object') {
    const err = error as { response?: { data?: { code?: string; message?: string; details?: Record<string, string> }; status?: number }; message?: string };

    // Структурированная ошибка от бэкенда с code
    if (err.response?.data?.code) {
      return err.response.data as APIError;
    }

    // Бэкенд вернул message без code — используем message напрямую
    if (err.response?.data?.message) {
      return { code: 'INTERNAL_ERROR', message: err.response.data.message };
    }

    // Fallback на HTTP статусы
    if (err.response?.status === 401) {
      return { code: 'UNAUTHORIZED', message: 'Требуется авторизация' };
    }
    if (err.response?.status === 503) {
      return { code: 'SERVICE_UNAVAILABLE', message: 'Сервис временно недоступен' };
    }
  }
  return { code: 'INTERNAL_ERROR', message: 'Произошла ошибка' };
}

export const useConnectionsStore = create<ConnectionsState>()((set, get) => ({
  connections: [],
  currentConnectionId: null,
  isLoading: false,
  error: null,
  pendingSyncInfo: null,

  fetchConnections: async () => {
    set({ isLoading: true, error: null });
    try {
      const [connectionsResponse, settingsResponse] = await Promise.all([
        getConnections(),
        getSettings().catch(() => ({ last_connection_id: null })),
      ]);

      const connections = connectionsResponse.connections;
      let currentId = settingsResponse.last_connection_id || null;

      // If no current connection set, use first non-pending-deletion connection
      if (!currentId && connections.length > 0) {
        const activeConnection = connections.find(c => !c.deletion_requested_at);
        currentId = activeConnection?.uuid || connections[0].uuid;
      }

      // Verify currentId exists in connections
      if (currentId && !connections.find(c => c.uuid === currentId)) {
        currentId = connections[0]?.uuid || null;
      }

      // Restore pendingSyncInfo if a connection has an active sync task
      // (backend only returns sync_task_id for pending/running tasks)
      const { pendingSyncInfo } = get();
      let restoredSyncInfo = pendingSyncInfo;
      if (!restoredSyncInfo) {
        const syncingConn = connections.find(c => c.sync_task_id);
        if (syncingConn) {
          restoredSyncInfo = {
            taskId: syncingConn.sync_task_id!,
            marketplace: syncingConn.marketplace,
          };
        }
      }

      set({
        connections,
        currentConnectionId: currentId,
        isLoading: false,
        pendingSyncInfo: restoredSyncInfo,
      });
    } catch (error) {
      set({
        error: parseAPIError(error),
        isLoading: false,
      });
      throw error;
    }
  },

  setCurrentConnection: async (uuid: string) => {
    const { connections } = get();
    const connection = connections.find(c => c.uuid === uuid);

    if (!connection) {
      throw new Error('Connection not found');
    }

    set({ currentConnectionId: uuid });

    // Persist to backend
    try {
      await updateSettings({ last_connection_id: uuid });
    } catch (error) {
      console.error('Failed to persist current connection:', error);
    }
  },

  addConnection: async (data: CreateConnectionRequest) => {
    set({ error: null });
    try {
      const connection = await createConnection(data);

      const { connections, currentConnectionId } = get();
      const newConnections = [connection, ...connections];

      const newCurrentId = currentConnectionId || connection.uuid;

      set({
        connections: newConnections,
        currentConnectionId: newCurrentId,
        pendingSyncInfo: connection.sync_task_id
          ? { taskId: connection.sync_task_id, marketplace: data.marketplace }
          : null,
      });

      return connection;
    } catch (error) {
      const apiError = parseAPIError(error);
      throw apiError;
    }
  },

  updateConnectionKey: async (uuid: string, data: UpdateKeyRequest) => {
    set({ isLoading: true, error: null });
    try {
      await updateKey(uuid, data);

      // Update local state
      const { connections } = get();
      const updatedConnections = connections.map(c =>
        c.uuid === uuid
          ? { ...c, key_status: 'active' as const, key_status_message: null }
          : c
      );

      set({ connections: updatedConnections, isLoading: false });
    } catch (error) {
      const apiError = parseAPIError(error);
      set({ error: apiError, isLoading: false });
      throw apiError;
    }
  },

  requestDeletion: async (uuid: string) => {
    set({ isLoading: true, error: null });
    try {
      await deleteConnection(uuid);

      // Update local state
      const { connections, currentConnectionId } = get();
      const updatedConnections = connections.map(c =>
        c.uuid === uuid
          ? { ...c, deletion_requested_at: new Date().toISOString() }
          : c
      );

      // If deleting current connection, switch to another
      let newCurrentId = currentConnectionId;
      if (currentConnectionId === uuid) {
        const otherConnection = updatedConnections.find(
          c => c.uuid !== uuid && !c.deletion_requested_at
        );
        newCurrentId = otherConnection?.uuid || null;
      }

      set({
        connections: updatedConnections,
        currentConnectionId: newCurrentId,
        isLoading: false,
      });

      // Persist new current connection
      if (newCurrentId !== currentConnectionId) {
        try {
          await updateSettings({ last_connection_id: newCurrentId });
        } catch (error) {
          console.error('Failed to update settings:', error);
        }
      }
    } catch (error) {
      const apiError = parseAPIError(error);
      set({ error: apiError, isLoading: false });
      throw apiError;
    }
  },

  cancelConnectionDeletion: async (uuid: string) => {
    set({ isLoading: true, error: null });
    try {
      await cancelDeletion(uuid);

      // Update local state
      const { connections } = get();
      const updatedConnections = connections.map(c =>
        c.uuid === uuid
          ? { ...c, deletion_requested_at: null }
          : c
      );

      set({ connections: updatedConnections, isLoading: false });
    } catch (error) {
      const apiError = parseAPIError(error);
      set({ error: apiError, isLoading: false });
      throw apiError;
    }
  },

  validateConnectionKey: async (data: ValidateKeyRequest) => {
    try {
      return await validateKey(data);
    } catch (error) {
      const apiError = parseAPIError(error);
      throw apiError;
    }
  },

  clearError: () => set({ error: null }),
  setPendingSyncInfo: (info) => set({ pendingSyncInfo: info }),
}));

// Selector hooks
export const useCurrentConnection = () => {
  const connections = useConnectionsStore(state => state.connections);
  const currentConnectionId = useConnectionsStore(state => state.currentConnectionId);
  return connections.find(c => c.uuid === currentConnectionId) || null;
};

export const useActiveConnections = () => {
  const connections = useConnectionsStore(state => state.connections);
  return connections.filter(c => !c.deletion_requested_at);
};

export const usePendingDeletionConnections = () => {
  const connections = useConnectionsStore(state => state.connections);
  return connections.filter(c => c.deletion_requested_at);
};

// Только шаред-подключения (от других пользователей)
export const useSharedConnections = () => {
  const connections = useConnectionsStore(state => state.connections);
  return connections.filter(c => c.is_shared && !c.deletion_requested_at);
};

// Только собственные подключения (не шаред)
export const useOwnConnections = () => {
  const connections = useConnectionsStore(state => state.connections);
  return connections.filter(c => !c.is_shared && !c.deletion_requested_at);
};
